import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';

import type { Payment } from '../../types/payment';
import { buildMetaMaskDeepLink } from '../../utils/crypto/toWei';
import {
  Wrap,
  Title,
  SubTitle,
  Text,
  Row,
  Btn,
  BoxQR,
  IconCopy,
  DesktopColumns,
  DetailsStack,
  QRPanel,
  MobileLayout,
  ActionsRow,
  QRWithMetaMask,
  MetaMaskBtn,
  MetaMaskIcon,
} from './styled';

export interface PaymentInstructionsProps {
  payment: Payment;
  onCancel: () => void;
  /** Hide internal cancel button (when parent provides its own) */
  hideCancel?: boolean;
}

const MOBILE_BREAKPOINT = 720;

const isWindowMobile = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.innerWidth < MOBILE_BREAKPOINT;
};

const WARNING_THRESHOLD_MS = 60000; // 60 seconds

/**
 * Format milliseconds to MM:SS countdown string.
 */
const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const PaymentInstructions: React.FC<PaymentInstructionsProps> = ({ payment, onCancel, hideCancel = false }) => {
  const { amount, currency, providerData } = payment;
  const tokenSymbol = providerData?.tokenSymbol ?? currency;
  const formattedAmount = [amount, tokenSymbol || currency].filter(Boolean).join(' ');
  const toAddress = providerData?.toAddress ?? '';
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(isWindowMobile);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (!payment.expiresAt) {
      setTimeRemaining(null);
      return undefined;
    }

    const updateCountdown = () => {
      const expiresAtMs = Date.parse(payment.expiresAt);
      const remaining = expiresAtMs - Date.now();
      setTimeRemaining(Math.max(0, remaining));
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [payment.expiresAt]);

  const isWarning = timeRemaining !== null && timeRemaining <= WARNING_THRESHOLD_MS && timeRemaining > 0;

  const handleCopyAddress = () => {
    if (!providerData?.toAddress) {
      return;
    }

    navigator.clipboard.writeText(providerData.toAddress).then(
      () => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to copy payment address', error);
      }
    );
  };


  const formattedExpiry = payment.expiresAt
    ? format(new Date(payment.expiresAt), "MMM d, yyyy 'at' HH:mm 'UTC'")
    : null;

  const copyButton = (
    <ActionsRow $fullWidth={isMobile}>
      <Btn type="button" onClick={handleCopyAddress} disabled={!providerData?.toAddress}>
        <IconCopy aria-hidden />
        {copied ? 'Copied!' : 'Copy Address'}
      </Btn>
    </ActionsRow>
  );

  const cancelButtonRow = (
    <ActionsRow $fullWidth={isMobile}>
      <Btn type="button" onClick={onCancel}>
        Cancel
      </Btn>
    </ActionsRow>
  );

  const detailsSections = (
    <DetailsStack>
      <section>
        <SubTitle>Amount</SubTitle>
        <Text>{formattedAmount}</Text>
      </section>

      <section>
        <SubTitle>Recipient address</SubTitle>
        <Text>{providerData?.toAddress ?? '—'}</Text>
        {copyButton}
      </section>

      <section>
        <SubTitle>Network</SubTitle>
        <Text>
          {providerData?.network ?? '—'}
          {tokenSymbol ? ` (${tokenSymbol})` : ''}
        </Text>
      </section>

      {formattedExpiry && (
        <section>
          <SubTitle>Expires at</SubTitle>
          <Text>{formattedExpiry}</Text>
        </section>
      )}

      {timeRemaining !== null && (
        <section>
          <SubTitle>Time remaining</SubTitle>
          <Text
            data-testid="countdown-timer"
            data-warning={isWarning || timeRemaining === 0 ? 'true' : undefined}
          >
            {formatCountdown(timeRemaining)}
          </Text>
        </section>
      )}
    </DetailsStack>
  );

  const chainId = providerData?.chainId ?? 137;
  const hasValidAddress = Boolean(toAddress);

  const handleMetaMaskClick = () => {
    if (!hasValidAddress || !amount) return;
    const metamaskUrl = buildMetaMaskDeepLink(toAddress, amount, chainId);
    const openedWindow = window.open(metamaskUrl, '_blank', 'noopener,noreferrer');

    if (openedWindow) {
      openedWindow.opener = null;
    }
  };

  const qrSection = (
    <QRPanel>
      <SubTitle>Scan QR</SubTitle>
      <QRWithMetaMask>
        <BoxQR data-testid="payment-qr" $fullWidth={isMobile}>
          <QRCodeSVG
            value={toAddress}
            size={isMobile ? 152 : 200}
            level="H"
            bgColor="#15191e"
            fgColor="#52D381"
            includeMargin
          />
        </BoxQR>
        {!isMobile && (
          <MetaMaskBtn
            type="button"
            onClick={handleMetaMaskClick}
            aria-label="Pay with MetaMask"
            aria-disabled={!hasValidAddress}
            disabled={!hasValidAddress}
            data-testid="metamask-button"
          >
            <MetaMaskIcon aria-hidden />
          </MetaMaskBtn>
        )}
      </QRWithMetaMask>
    </QRPanel>
  );

  return (
    <Wrap
      data-testid={isMobile ? 'payment-layout-mobile' : 'payment-layout-desktop'}
      $isMobile={isMobile}
    >
      <Title>Payment Instructions</Title>

      {isMobile ? (
        <MobileLayout>
          {detailsSections}
          {qrSection}
          {!hideCancel && cancelButtonRow}
        </MobileLayout>
      ) : (
        <>
          <DesktopColumns>
            {detailsSections}
            {qrSection}
          </DesktopColumns>
          {!hideCancel && cancelButtonRow}
        </>
      )}
    </Wrap>
  );
};

export default PaymentInstructions;
export { PaymentInstructions };
